import React, { useMemo, useState } from 'react';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  type SxProps,
} from '@mui/material';
import { identity, isEmpty, isNil } from 'lodash/fp';
import { KebabIcon } from '@/static';
import { normalizeRows, type NormalizeRowsInput } from './utils';
import styles from './styles';

type TableActionConfig = {
  id: string;
  title: string;
  Icon: React.ElementType;
  onClick: (row: any) => () => void;
  sx?: SxProps;
};

type TableAction =
  | ((row: any) => TableActionConfig | false)
  | TableActionConfig;

type Props = NormalizeRowsInput & {
  actions: TableAction[];
  cellFormatters?: Record<
    string,
    (value: any, header: string, row: any) => any
  >;
};

const CustomTable = ({
  headers,
  headerMapping,
  rows,
  actions,
  cellFormatters,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);

  const { columns, headRows, bodyRows } = useMemo(
    () => normalizeRows({ headers, headerMapping, rows }),
    [headers, headerMapping, rows],
  );

  const processedRowActions = useMemo(() => {
    if (!rows || isEmpty(actions)) return [];

    return rows.map((row) => {
      const rowActions = actions
        .map((actionConfig) => {
          const action =
            typeof actionConfig === 'function'
              ? actionConfig(row)
              : actionConfig;
          return action;
        })
        .filter(Boolean) as TableActionConfig[];

      return rowActions;
    });
  }, [rows, actions]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    rowIndex: number,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowIndex(rowIndex);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowIndex(null);
  };

  const isMenuOpen = Boolean(anchorEl);

  if (rows?.length === 0) {
    return <Box sx={styles.noData}>No data found</Box>;
  }

  return (
    <TableContainer component={Paper} sx={styles.tableWrapper}>
      <Table sx={styles.table} aria-label="simple table">
        <TableHead sx={styles.tableHead}>
          <TableRow>
            {headRows.map((header) => (
              <TableCell key={header} align="left" sx={styles.th}>
                {header}
              </TableCell>
            ))}

            {!isEmpty(actions) && <TableCell sx={styles.th} />}
          </TableRow>
        </TableHead>

        <TableBody>
          {!isNil(rows)
            ? bodyRows?.map((row, rowIndex) => (
                <TableRow key={rowIndex} sx={styles.tableRow}>
                  {row.map((cell, cellIndex) => {
                    const formatter =
                      cellFormatters?.[columns[cellIndex]] || identity;

                    return (
                      <TableCell
                        key={cellIndex}
                        sx={styles.tableCell}
                        align="left"
                      >
                        {formatter(cell, columns[cellIndex], rows?.[rowIndex])}
                      </TableCell>
                    );
                  })}

                  {!isEmpty(actions) && (
                    <TableCell sx={styles.tableCell} align="right">
                      {processedRowActions[rowIndex]?.length > 0 && (
                        <>
                          <IconButton
                            onClick={(event) => handleMenuOpen(event, rowIndex)}
                            sx={{
                              ...styles.kebabIcon,
                              ...(isMenuOpen && selectedRowIndex === rowIndex
                                ? styles.kebabIconActive
                                : {}),
                            }}
                          >
                            <KebabIcon />
                          </IconButton>

                          <Menu
                            anchorEl={anchorEl}
                            open={isMenuOpen && selectedRowIndex === rowIndex}
                            onClose={handleMenuClose}
                            transformOrigin={{
                              horizontal: 'right',
                              vertical: 'top',
                            }}
                            anchorOrigin={{
                              horizontal: 'right',
                              vertical: 'bottom',
                            }}
                            sx={styles.actionsMenu}
                          >
                            {processedRowActions[rowIndex].map((action) => (
                              <MenuItem
                                key={action.id}
                                onClick={() => {
                                  action.onClick(rows?.[rowIndex])();
                                  handleMenuClose();
                                }}
                                sx={{
                                  ...styles.actionsMenuItem,
                                  ...(action.sx || {}),
                                }}
                              >
                                <ListItemIcon>
                                  <action.Icon />
                                </ListItemIcon>

                                <ListItemText primary={action.title} />
                              </MenuItem>
                            ))}
                          </Menu>
                        </>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            : Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: headRows?.length }).map(
                    (_, cellIndex) => (
                      <TableCell key={cellIndex} sx={styles.tableCell}>
                        <Skeleton
                          sx={cellIndex !== 0 ? styles.skeletonRight : {}}
                          width={cellIndex === 0 ? 100 : 75}
                        />
                      </TableCell>
                    ),
                  )}

                  {!isEmpty(actions) && (
                    <TableCell sx={styles.tableCell} align="right">
                      <Skeleton
                        variant="circular"
                        sx={styles.skeletonAction}
                        width={24}
                        height={24}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CustomTable;
